import type { InertTag, TabId } from "../inspector/schema";
import { GROUPS } from "../sections/groups";

/** Left library panels — the reference's left icon rail. */
export type LeftPanelId =
  // Selection-gated material libraries, inserted at the TOP when a mesh is
  // selected and gone again when it is not.
  //
  // The reference splits these five ways; we group to three. A ceramic IS a
  // metal finish as far as picking a surface goes, and a pearl sits with the
  // gems — five panels for 451 presets was a taxonomy, not a workflow.
  // ⚠️ UI GROUPING ONLY: the stored PresetKind stays `ceramic` / `pearl`, so
  // nothing needs a data migration and this reverts by editing one array.
  | "metals"
  | "gems"
  | "otherMaterials"
  | "parts"
  | "environment"
  | "gemEnv"
  | "background"
  | "scene"
  | "modelStage"
  | "ground"
  | "poses"
  | "export"
  | "batchRenderer"
  | "onBody"
  | "embed"
  | "branding"
  | "advanced";

export interface LeftRailItem {
  id: LeftPanelId;
  icon: string;
  label: string;
  /** Only offered while something is selected — there is nothing to apply a
   *  material to otherwise, and the reference hides these entries too. */
  requiresSelection?: true;
  /** Audit marker for a panel the dead-control inventory flagged; shown as a
   *  chip in the panel header. */
  inert?: InertTag;
  /** A duplicate or developer panel: on the rail only with "Show experimental". */
  experimental?: true;
}

/** Material Symbols glyphs chosen to mirror iJewel's left rail. */
export const LEFT_RAIL: LeftRailItem[] = [
  { id: "metals", icon: "circle", label: "Metals", requiresSelection: true },
  { id: "gems", icon: "diamond", label: "Gems", requiresSelection: true },
  { id: "otherMaterials", icon: "grain", label: "Other", requiresSelection: true },
  { id: "parts", icon: "category", label: "Parts" },
  { id: "environment", icon: "panorama_photosphere", label: "HDRIs" },
  { id: "gemEnv", icon: "diamond", label: "Gem light" },
  { id: "background", icon: "wallpaper", label: "Backdrops" },
  { id: "scene", icon: "auto_awesome", label: "Looks" },
  { id: "modelStage", icon: "deployed_code", label: "Stage" },
  { id: "ground", icon: "grid_on", label: "Floors" },
  {
    id: "poses",
    experimental: true,
    icon: "accessibility_new",
    label: "Poses",
    inert: {
      label: "duplicate",
      reason:
        "Compute / Drop to Ground are real, but this panel is a second copy of Animations > Pose Plugin with its own local state.",
    },
  },
  { id: "export", icon: "output", label: "Exports" },
  { id: "batchRenderer", icon: "burst_mode", label: "Batch" },
  { id: "onBody", icon: "accessibility", label: "On Body" },
  { id: "embed", icon: "code", label: "Embed" },
  { id: "branding", icon: "sell", label: "Branding" },
  {
    id: "advanced",
    experimental: true,
    icon: "tune",
    // Not "Advanced": that is the mode switch's word, one row above.
    label: "Env extras",
    inert: {
      label: "duplicate",
      reason:
        "Env rotation / intensity are a third independent mirror of Scene > Background & Environment: each seeds its own local state once, so editing one leaves the others stale. The two checkboxes are the exception — “Fixed Env Direction” and “Tonemap Background + Ground” read and write the shared store slices, so they stay in step everywhere (the tone-mapping one moves both flags together; splitting them seams the frame at the horizon).",
    },
  },
];

/**
 * Basic mode's left rail — since 2026-10-04 the reference's full preset panel,
 * asked for in the user's words: "Now in basic also i want the full present
 * panel like [iJewel's]". Their rail reads Metal / Gem / Extra Material /
 * Scene / Metal HDR / Gem HDR / Background Presets and AI Match; ours, in the
 * same order: the five material libraries, Scenes, the two lighting libraries,
 * Background and AI Match. Then a divider and the three jobs Basic already had
 * (Camera · Export · Share), unchanged — the user chose to keep them.
 *
 * Every panel changes the scene through the same engine and RPCs Advanced
 * uses, so nothing here owns a value.
 */
export type BasicLeftPanelId =
  | "metals"
  | "gems"
  | "ceramics"
  | "pearls"
  | "otherMaterials"
  | "scenes"
  | "metalHdr"
  | "gemHdr"
  | "backdrop"
  | "aiMatch"
  | "camera"
  | "output"
  | "share";

export interface BasicRailItem {
  id: BasicLeftPanelId;
  icon: string;
  label: string;
  /** Draw the divider above this item (it starts the jobs group). */
  dividerBefore?: true;
}

export const BASIC_LEFT_RAIL: BasicRailItem[] = [
  { id: "metals", icon: "circle", label: "Metals" },
  { id: "gems", icon: "diamond", label: "Gems" },
  { id: "ceramics", icon: "contrast", label: "Ceramics" },
  { id: "pearls", icon: "bubble_chart", label: "Pearls" },
  { id: "otherMaterials", icon: "grain", label: "Other" },
  { id: "scenes", icon: "style", label: "Scenes" },
  // "Metal HDR" / "Gem HDR" are the user's own words (see the jargon guard).
  { id: "metalHdr", icon: "panorama_photosphere", label: "Metal HDR" },
  { id: "gemHdr", icon: "flare", label: "Gem HDR" },
  // "Background", not "Backgrounds": the plural measures 68.9px in the rail's
  // 11px/600 Inter against a 64px label box and breaks mid-word.
  { id: "backdrop", icon: "wallpaper", label: "Background" },
  { id: "aiMatch", icon: "auto_awesome", label: "AI Match" },
  { id: "camera", icon: "photo_camera", label: "Camera", dividerBefore: true },
  { id: "output", icon: "download", label: "Export" },
  { id: "share", icon: "share", label: "Share" },
];

/** Panels Basic can show on the left: exactly the rail's. */
export const BASIC_LEFT_PANEL_IDS: BasicLeftPanelId[] = BASIC_LEFT_RAIL.map((r) => r.id);

export function isBasicLeftPanel(id: string | null | undefined): id is BasicLeftPanelId {
  return !!id && (BASIC_LEFT_PANEL_IDS as string[]).includes(id);
}

/**
 * Basic panel ids retired by the 2026-10-04 rail, and what replaced each. A
 * saved `leftPanel` still holding one is re-pointed when the ui state loads
 * (`ui.ts`, schema 5). None of the old ids was ever an Advanced panel id, so
 * mapping them cannot catch an Advanced save.
 */
export const RETIRED_BASIC_PANELS: Record<string, BasicLeftPanelId> = {
  looks: "scenes",
  metal: "metals",
  stones: "gems",
};

export interface RightRailItem {
  id: TabId;
  icon: string;
  label: string;
  /** Tailwind text color class for the icon (iJewel color-codes its rail). */
  accent: string;
  /** Audit marker for a tab the dead-control inventory flagged; shown as a chip
   *  in the inspector header. */
  inert?: InertTag;
}

/**
 * Advanced's right rail: our own groups (sections/groups.ts), in order. The
 * iJewel tabs — and the "Editor" tab that repeated every other tab — are gone;
 * every section still exists, regrouped.
 */
export const RIGHT_RAIL: RightRailItem[] = GROUPS.map((g) => ({ id: g.id, icon: g.icon, label: g.label, accent: "" }));

/**
 * The rail as it should render right now.
 *
 * The five material panels exist only while a mesh is selected. Anything that
 * RESOLVES an open panel must go through this rather than LEFT_RAIL, or a
 * persisted `leftPanel: "metals"` would still find its entry after the
 * selection was cleared and render a panel with nothing to apply to.
 */
export function visibleLeftRail(hasSelection: boolean, showExperimental = true): LeftRailItem[] {
  return LEFT_RAIL.filter((i) => (hasSelection || !i.requiresSelection) && (showExperimental || !i.experimental));
}
